
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, FileText, Video, CreditCard, Users, AlertCircle, Calendar } from 'lucide-react';

const TheWallSection = () => {
  const { isMobile } = useBreakpoint();

  const obstacles = [
    {
      icon: FileText,
      title: "Professional Landing Page",
      description: "Design, copy, optimization, mobile responsiveness",
      timeEstimate: "2-3 weeks"
    },
    {
      icon: FileText,
      title: "Compelling Sales Copy",
      description: "Headlines, bullet points, objection handling, CTAs",
      timeEstimate: "1-2 weeks"
    },
    {
      icon: Video,
      title: "Video Sales Letter",
      description: "Script writing, filming, editing, hosting",
      timeEstimate: "3-4 weeks"
    },
    {
      icon: CreditCard,
      title: "Business Card Templates",
      description: "Design variations, print-ready files, branding",
      timeEstimate: "1 week"
    }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-gray-50 via-white to-gray-100" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="subtle-grid"
      patternColor="#003a70"
      patternOpacity={2}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
            <AlertCircle className="text-[#003a70]" size={isMobile ? 24 : 28} />
            <span className="text-[#003a70] font-bold text-lg md:text-xl">The Reality Check</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            "Now I Have to Create 
            <br />
            <span className="text-red-600">All The Sales Materials..."</span>
          </h2>
          
          <p className={cn(
            "text-[#003a70]/80 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            This is where most entrepreneurs hit <span className="font-bold text-red-600">THE WALL</span>
          </p>
        </div>

        {/* The Wall Visualization */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="relative bg-white rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/30 shadow-2xl max-w-6xl mx-auto overflow-hidden">
            {/* Brick wall pattern overlay */}
            <div className="absolute inset-0 opacity-5 bg-gradient-to-br from-[#003a70] to-[#003a70]/60" style={{
              backgroundImage: `repeating-linear-gradient(
                0deg,
                transparent,
                transparent 20px,
                rgba(0, 58, 112, 0.1) 20px,
                rgba(0, 58, 112, 0.1) 21px
              ),
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 40px,
                rgba(0, 58, 112, 0.1) 40px,
                rgba(0, 58, 112, 0.1) 41px
              )`
            }}></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <AlertCircle className="text-white" size={32} />
                </div>
              </div>
              
              <h3 className={cn(
                "font-bold text-[#003a70] mb-8 leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                Here's What You Actually Need to Create:
              </h3>
              
              {/* Obstacles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
                {obstacles.map((obstacle, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-[#FEE6D8]/20 to-white rounded-2xl p-6 border border-[#FEE6D8]/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/80 rounded-xl flex items-center justify-center flex-shrink-0">
                        <obstacle.icon className="text-[#003a70]" size={24} />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="font-bold text-[#003a70] mb-2 text-lg">
                          {obstacle.title}
                        </h4>
                        <p className="text-[#003a70]/70 text-sm leading-relaxed mb-3">
                          {obstacle.description}
                        </p>
                        <div className="flex items-center gap-2">
                          <Clock className="text-red-500" size={16} />
                          <span className="text-red-600 font-semibold text-sm">
                            {obstacle.timeEstimate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Total Time Reality */}
              <div className="bg-gradient-to-br from-[#FEE6D8]/30 via-white to-[#FEE6D8]/20 rounded-3xl p-8 border-2 border-[#FEE6D8]/50 shadow-lg">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                    <Calendar className="text-white" size={26} />
                  </div>
                </div>
                
                <h4 className={cn(
                  "font-bold text-[#003a70] mb-4 leading-tight",
                  isMobile ? "text-xl" : "text-2xl lg:text-3xl"
                )}>
                  The Harsh Math:
                </h4>
                
                <div className="space-y-4">
                  <p className={cn(
                    "text-red-600 font-bold leading-relaxed",
                    isMobile ? "text-xl" : "text-2xl lg:text-3xl"
                  )}>
                    7-10 weeks minimum
                  </p>
                  
                  <p className={cn(
                    "text-[#003a70]/80 leading-relaxed",
                    isMobile ? "text-base" : "text-lg"
                  )}>
                    That's assuming you work on this <span className="font-bold">every single day</span>, 
                    know exactly what you're doing, and don't get stuck on design decisions or copywriting.
                  </p>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-[#FEE6D8]/30 shadow-inner mt-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="text-[#003a70]" size={20} />
                      <span className="font-semibold text-[#003a70]">Reality for Most Entrepreneurs:</span>
                    </div>
                    <ul className="space-y-3 text-left text-[#003a70]/80">
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>You're running a business, so you don't have 8 hours a day for this</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>You get stuck on design and copy decisions</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>You second-guess everything and start over multiple times</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="font-bold text-red-600">3-6 months later, you still haven't launched</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default TheWallSection;
